import React, { useMemo } from 'react';
import { Modal } from 'antd';
import { useLocations } from '@core/hooks/useLocations';
import { useStoreContext } from '@core/hooks/useStoreContext';
import { useCopy } from '@/app/providers/useCopy';
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const toMinutes = (timeString) => {
  const [hours, minutes] = (timeString || '').split(':').map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};
export const formatTime12Hour = (time) => {
  if (!time) return '';

  const [hours, minutes] = time.split(':').map(Number);

  return new Date(0, 0, 0, hours, minutes).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};
const getGroupedTimings = (store) => {
  const timings = store?.timings || {};
  if (!Array.isArray(timings)) return timings;
  return timings.reduce((grouped, scheduleItem) => {
    if (scheduleItem?.day) {
      const dayName = String(scheduleItem.day).toLowerCase();
      (grouped[dayName] = grouped[dayName] || []).push(scheduleItem);
    }
    return grouped;
  }, {});
};
export function storeClosingTime(store) {
  if (!store) return '';
  const now = new Date();
  const day = WEEKDAYS[now.getDay()];
  const min = now.getHours() * 60 + now.getMinutes();
  const timings = getGroupedTimings(store);
  const yesterdayDay = WEEKDAYS[(now.getDay() + 6) % 7];
  const activeTiming =
    (timings[day] || []).find((item) => {
      const startMinutes = toMinutes(item.start_time),
        endMinutes = toMinutes(item.end_time);
      return endMinutes < startMinutes
        ? min >= startMinutes
        : min >= startMinutes && min <= endMinutes;
    }) ||
    (timings[yesterdayDay] || []).find((item) => {
      const startMinutes = toMinutes(item.start_time),
        endMinutes = toMinutes(item.end_time);
      return endMinutes < startMinutes && min <= endMinutes;
    });
  if (!activeTiming) return '';
  const startMinutes = toMinutes(activeTiming.start_time),
    endMinutes = toMinutes(activeTiming.end_time);
  const relativeDayText = endMinutes < startMinutes && min >= startMinutes ? 'Tomorrow' : 'Today';
  return `closes ${relativeDayText} at ${formatTime12Hour(activeTiming.end_time)}`;
}
export function storeNextOpeningTime(store) {
  if (!store) return '';
  if (storeClosingTime(store) !== '') return '';
  const now = new Date();
  const min = now.getHours() * 60 + now.getMinutes();
  const timings = getGroupedTimings(store);
  const dayIdx = now.getDay();
  for (let i = 0; i < WEEKDAYS.length; i++) {
    const dayName = WEEKDAYS[(dayIdx + i) % 7];
    const sortedTimings = [...(timings[dayName] || [])].sort(
      (a, b) => toMinutes(a.start_time) - toMinutes(b.start_time),
    );
    for (const item of sortedTimings) {
      const startMinutes = toMinutes(item.start_time);
      if (i === 0 && min >= startMinutes) continue;
      const relativeDayText =
        i === 0
          ? 'Today'
          : i === 1
            ? 'Tomorrow'
            : dayName.charAt(0).toUpperCase() + dayName.slice(1);
      return `opens again ${relativeDayText} at ${formatTime12Hour(item.start_time)}`;
    }
  }
  return '';
}
export function useStoreClosed() {
  const { data: locations = [] } = useLocations();
  const { locationId } = useStoreContext();
  const t = useCopy();
  const storeStatus = useMemo(() => {
    const activeLocation = locations.find((loc) => loc.id === locationId) ?? locations[0] ?? null;
    if (!activeLocation) return null;
    return {
      isOpen: storeClosingTime(activeLocation) !== '',
      nextOpenTime: storeNextOpeningTime(activeLocation).replace('opens again ', ''),
    };
  }, [locations, locationId]);
  const isClosed = storeStatus ? !storeStatus.isOpen : false;
  const checkStoreOpenOrShowModal = () => {
    if (isClosed) {
      const nextOpen = storeStatus?.nextOpenTime;
      const baseMessage = t(
        'storeClosedMessage',
        'Orders cannot be processed while the store is closed.',
      );
      const content = nextOpen
        ? React.createElement(
            'span',
            null,
            `${baseMessage} Please try again on `,
            React.createElement('span', { className: 'next-open-time' }, nextOpen),
            '.',
          )
        : baseMessage;
      Modal.error({
        className: 'app-modal app-modal--confirm store-closed-modal',
        zIndex: 10000,
        title: t('storeClosedTitle', 'Store Temporarily Closed'),
        content,
        icon: null,
        closable: true,
        maskClosable: true,
        footer: null,
      });
      return false;
    }
    return true;
  };
  return {
    isClosed,
    storeStatus,
    checkStoreOpenOrShowModal,
  };
}
